import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Send, Trash2, Mail, MessageSquare, Package, RotateCcw, Sparkles, CalendarClock, X } from "lucide-react";
import { toast } from "sonner";

const CHANNEL_META = {
  sms: { label: "SMS", icon: MessageSquare, badge: "bg-blue-100 text-blue-800 border-blue-200" },
  email: { label: "Email", icon: Mail, badge: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  direct_mail: { label: "Direct Mail", icon: Package, badge: "bg-amber-100 text-amber-800 border-amber-200" },
};

const AUDIENCES = [
  { key: "all", label: "All leads" },
  { key: "new_lead", label: "New leads only" },
  { key: "contacted", label: "Contacted only" },
  { key: "quote_sent", label: "Quote Sent only" },
  { key: "follow_up", label: "Follow-Up only" },
  { key: "booked", label: "Booked only" },
  { key: "completed", label: "Completed only" },
  { key: "lost", label: "Lost only (reactivation)" },
];

const DEFAULT_FORM = { name: "", channel: "email", subject: "", message: "", audience: "all", status: "draft", scheduled_send_at: "" };

export default function Campaigns() {
  const [items, setItems] = useState([]);
  const [segments, setSegments] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);

  const load = async () => {
    const [{ data: c }, { data: s }] = await Promise.all([
      api.get("/campaigns"),
      api.get("/segments/counts"),
    ]);
    setItems(c);
    setSegments(s);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { scheduled_send_at, ...payload } = form;
      const { data: created } = await api.post("/campaigns", payload);
      if (scheduled_send_at) {
        // Convert local datetime-local to ISO UTC
        const iso = new Date(scheduled_send_at).toISOString();
        await api.post(`/campaigns/${created.id}/schedule`, { scheduled_send_at: iso });
        toast.success("Campaign scheduled");
      } else {
        toast.success("Campaign created");
      }
      setOpen(false);
      setForm(DEFAULT_FORM);
      load();
    } catch { toast.error("Failed"); }
  };

  const openReactivation = () => {
    setForm({
      name: "Reactivate lost leads",
      channel: "email",
      subject: "We'd love to earn your project back",
      message: "Hi there,\n\nWe wanted to reach out with an updated offer that might work better for you. Reply anytime if you're ready to revisit the project.\n\n— Your friends at LeadForge",
      audience: "lost",
      status: "draft",
    });
    setOpen(true);
  };

  const sendNow = async (c) => {
    const audienceLabel = AUDIENCES.find((a) => a.key === (c.audience || "all"))?.label || "all leads";
    if (!window.confirm(`Send "${c.name}" now to: ${audienceLabel}?`)) return;
    try {
      const { data } = await api.post(`/campaigns/${c.id}/send`);
      toast.success(`Sent to ${data.sent} lead(s) via ${data.channel.replace("_"," ")}`);
      load();
    } catch { toast.error("Send failed"); }
  };

  const remove = async (c) => {
    if (!window.confirm("Delete campaign?")) return;
    try { await api.delete(`/campaigns/${c.id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); }
  };

  const unschedule = async (c) => {
    try { await api.post(`/campaigns/${c.id}/unschedule`); toast.success("Schedule canceled"); load(); } catch { toast.error("Failed"); }
  };

  const lostCount = segments.lost || 0;
  const completedCount = segments.completed || 0;

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">Marketing</div>
          <h1 className="font-heading text-4xl font-bold">Campaigns</h1>
        </div>
        <Button onClick={()=>{ setForm(DEFAULT_FORM); setOpen(true); }} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift" data-testid="campaigns-new-btn">
          <Plus className="w-4 h-4 mr-1"/> New campaign
        </Button>
      </div>

      {/* Segment hero: Reactivation */}
      <div className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-white p-6 mb-8 relative overflow-hidden" data-testid="reactivation-segment-card">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-12 h-12 rounded-xl bg-indigo-600 flex items-center justify-center shrink-0">
            <RotateCcw className="w-6 h-6 text-white" strokeWidth={2.2}/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-mono-accent uppercase tracking-widest text-indigo-700 mb-1">Segment · Reactivation</div>
            <h2 className="font-heading text-2xl font-bold">Win back {lostCount} lost lead{lostCount === 1 ? "" : "s"}</h2>
            <p className="text-sm text-zinc-600 mt-1 max-w-2xl">
              Broadcast an updated offer or check-in to every lead marked as <span className="font-medium">Lost</span>. Their per-lead 30-day reactivation is already scheduled — this is your bulk lever for the whole segment.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="grid grid-cols-2 gap-2">
              <SegmentPill label="Lost" value={lostCount} accent="indigo" testid="segment-pill-lost"/>
              <SegmentPill label="Completed" value={completedCount} accent="emerald" testid="segment-pill-completed"/>
            </div>
            <Button
              onClick={openReactivation}
              disabled={lostCount === 0}
              className="bg-indigo-600 hover:bg-indigo-700 text-white btn-lift"
              data-testid="reactivation-launch-btn"
            >
              <Sparkles className="w-4 h-4 mr-1"/> Launch reactivation
            </Button>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((c) => {
          const meta = CHANNEL_META[c.channel];
          const Icon = meta.icon;
          const audienceLabel = AUDIENCES.find((a) => a.key === (c.audience || "all"))?.label || "All leads";
          const audCount = segments[c.audience] ?? (c.audience === "all" ? segments.all : 0);
          const isReactivation = c.audience === "lost";
          return (
            <div key={c.id} className={`bg-white border rounded-xl p-6 card-crisp ${isReactivation ? "border-indigo-200" : "border-zinc-200"}`} data-testid={`campaign-card-${c.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isReactivation ? "bg-indigo-50" : "bg-orange-50"}`}>
                    <Icon className={`w-5 h-5 ${isReactivation ? "text-indigo-600" : "text-[#FF5A00]"}`}/>
                  </div>
                  <div>
                    <div className="font-heading font-bold text-lg leading-tight">{c.name}</div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      <span className={`inline-block px-2 py-0.5 text-xs border rounded ${meta.badge}`}>{meta.label}</span>
                      <span className={`inline-block px-2 py-0.5 text-xs border rounded ${isReactivation ? "bg-indigo-100 text-indigo-700 border-indigo-200" : "bg-zinc-100 text-zinc-700 border-zinc-200"}`}>{audienceLabel}</span>
                    </div>
                  </div>
                </div>
                <button onClick={()=>remove(c)} className="text-zinc-400 hover:text-red-600 p-1" data-testid={`campaign-delete-${c.id}`}><Trash2 className="w-4 h-4"/></button>
              </div>
              {c.subject && <div className="text-xs text-zinc-500 mb-1">Subject: {c.subject}</div>}
              <div className="text-sm text-zinc-700 line-clamp-3 whitespace-pre-wrap">{c.message}</div>
              <div className="mt-4 pt-4 border-t border-zinc-100 grid grid-cols-3 gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-accent">Audience</div>
                  <div className="font-mono-accent text-xl font-bold">{audCount ?? "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-accent">Sent</div>
                  <div className="font-mono-accent text-xl font-bold">{c.sent_count || 0}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-accent">Attributed</div>
                  <div className="font-mono-accent text-xl font-bold">{c.attributed_leads || 0}</div>
                </div>
              </div>
              <Button onClick={()=>sendNow(c)} disabled={c.status === "scheduled"} className={`w-full mt-4 text-white btn-lift ${isReactivation ? "bg-indigo-600 hover:bg-indigo-700" : "bg-zinc-900 hover:bg-zinc-800"} disabled:opacity-50 disabled:cursor-not-allowed`} data-testid={`campaign-send-${c.id}`}>
                <Send className="w-4 h-4 mr-1"/> {c.status === "scheduled" ? "Waiting for scheduler" : "Send now"}
              </Button>
              {c.status === "scheduled" && c.scheduled_send_at && (
                <div className="mt-3 flex items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2" data-testid={`campaign-scheduled-badge-${c.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <CalendarClock className="w-4 h-4 text-amber-700 shrink-0"/>
                    <div className="text-xs min-w-0">
                      <div className="text-amber-800 font-medium">Scheduled to send</div>
                      <div className="font-mono-accent text-amber-700 truncate">{new Date(c.scheduled_send_at).toLocaleString()}</div>
                    </div>
                  </div>
                  <button onClick={()=>unschedule(c)} className="text-amber-800 hover:text-amber-900 p-1" title="Cancel schedule" data-testid={`campaign-unschedule-${c.id}`}>
                    <X className="w-4 h-4"/>
                  </button>
                </div>
              )}
              {c.status === "active" && c.sent_at && (
                <div className="mt-2 text-[10px] text-zinc-500 text-center font-mono-accent">
                  Last sent {new Date(c.sent_at).toLocaleString()}
                </div>
              )}
              {c.channel === "sms" && <div className="text-[10px] text-zinc-400 text-center mt-2 font-mono-accent">SMS delivery follows the tenant setting in Settings → SMS automations</div>}
              {c.channel === "direct_mail" && <div className="text-[10px] text-zinc-400 text-center mt-2 font-mono-accent">Direct mail is tracked only</div>}
            </div>
          );
        })}
        {items.length === 0 && (
          <div className="col-span-full text-center py-16 bg-white border border-dashed border-zinc-300 rounded-xl">
            <div className="text-zinc-500">No campaigns yet.</div>
            <Button onClick={()=>{setForm(DEFAULT_FORM); setOpen(true);}} className="mt-4 bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift">Create your first</Button>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="campaign-form-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{form.audience === "lost" ? "Launch reactivation campaign" : "New campaign"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input required value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="campaign-form-name"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Channel</Label>
                <Select value={form.channel} onValueChange={(v)=>setForm({...form, channel: v})}>
                  <SelectTrigger data-testid="campaign-form-channel"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                    <SelectItem value="direct_mail">Direct Mail (track only)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Audience segment</Label>
                <Select value={form.audience} onValueChange={(v)=>setForm({...form, audience: v})}>
                  <SelectTrigger data-testid="campaign-form-audience"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {AUDIENCES.map((a) => (
                      <SelectItem key={a.key} value={a.key}>
                        {a.label} {segments[a.key] != null && a.key !== "all" ? `(${segments[a.key]})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.channel === "email" && (
              <div>
                <Label>Subject</Label>
                <Input value={form.subject} onChange={(e)=>setForm({...form, subject: e.target.value})} data-testid="campaign-form-subject"/>
              </div>
            )}
            <div>
              <Label>Message / body</Label>
              <Textarea rows={5} required value={form.message} onChange={(e)=>setForm({...form, message: e.target.value})} data-testid="campaign-form-message"/>
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <CalendarClock className="w-4 h-4 text-amber-700"/>
                Schedule for later <span className="text-xs text-zinc-500 font-normal">(optional)</span>
              </Label>
              <Input
                type="datetime-local"
                value={form.scheduled_send_at}
                onChange={(e)=>setForm({...form, scheduled_send_at: e.target.value})}
                data-testid="campaign-form-schedule-datetime"
              />
              <p className="text-[11px] text-zinc-500 mt-1">Leave empty to save as draft. Tuesdays 10am tend to convert best for reactivation.</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift" data-testid="campaign-form-submit">
                {form.scheduled_send_at ? "Schedule campaign" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SegmentPill({ label, value, accent, testid }) {
  const map = {
    indigo: "bg-indigo-100 text-indigo-700 border-indigo-200",
    emerald: "bg-emerald-100 text-emerald-700 border-emerald-200",
  };
  return (
    <div className={`px-3 py-1.5 rounded-lg border ${map[accent]}`} data-testid={testid}>
      <div className="text-[9px] uppercase font-mono-accent tracking-widest opacity-80">{label}</div>
      <div className="font-heading text-lg font-bold leading-none">{value}</div>
    </div>
  );
}
